import type { ReactNode } from 'react';
import { Box, Container, Flex, Group, Paper, Stack, Text, UnstyledButton } from '@mantine/core';
import { Link } from 'react-router-dom';
import { useMediaQuery } from '@mantine/hooks';
import { useAuth } from '../../../auth/AuthContext.js';
import { useClientNav } from '../../../components/client/ClientShell.js';
import {
  NewsIcon, BagIcon, WalletIcon, ChartIcon, UserIcon, ChevronRightIcon,
} from '../../../components/client/clientIcons.js';
import { HeroCarousel, type HeroSlide } from './HeroCarousel.js';
import { HeroPushButton } from './HeroPushButton.js';
import { StatCards } from './StatCards.js';
import { LatestNews } from './LatestNews.js';
import heroBg from '../../../assets/auth/bg.jpg';

const MENU: Array<{ to: string; label: string; sub: string; icon: ReactNode; needsSales?: boolean }> = [
  { to: '/news', label: 'Новости', sub: 'Актуальные события и обновления', icon: <NewsIcon size={22} /> },
  { to: '/client/orders', label: 'Мои заказы', sub: 'История заказов и статусы', icon: <BagIcon size={22} /> },
  { to: '/client/finance', label: 'Финансы', sub: 'Документы и взаиморасчёты', icon: <WalletIcon size={22} /> },
  { to: '/client/sales', label: 'Аналитика продаж', sub: 'Отчёты и ключевые показатели', icon: <ChartIcon size={22} />, needsSales: true },
  { to: '/profile', label: 'Мой профиль', sub: 'Личные данные и настройки', icon: <UserIcon size={22} /> },
];

function MenuItem({ to, label, sub, icon, compact }: {
  to: string; label: string; sub: string; icon: ReactNode; compact: boolean;
}) {
  return (
    <UnstyledButton
      component={Link}
      to={to}
      px="md"
      py={compact ? 8 : 14}
      style={{ borderRadius: 12 }}
      className="client-menu-item"
    >
      <Group wrap="nowrap" gap="md">
        <Box
          style={{
            width: compact ? 40 : 46, height: compact ? 40 : 46, borderRadius: 12, background: '#E6F7F3',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0FAC97', flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Text fw={600} fz={16} c="#14201B">{label}</Text>
          <Text fz={13.5} c="#6B7570">{sub}</Text>
        </Box>
        <Box style={{ color: '#B3BBB8', flexShrink: 0 }}><ChevronRightIcon /></Box>
      </Group>
    </UnstyledButton>
  );
}

export function ClientHome() {
  const { state } = useAuth();
  const { hasSales } = useClientNav();
  const isDesktop = useMediaQuery('(min-width: 62em)', false, { getInitialValueInEffect: false });
  // Компактный режим: ниже 1150px вьюпорта (1080p и ноутбуки с панелями браузера)
  // ужимаем вертикальные отступы, чтобы вся главная влезала без прокрутки.
  // Порог согласован с высотами блоков: полный режим (~830px контента без hero +
  // hero 320) помещается только от 1150px; компактный (~690px + гибкий hero) — от ~860px.
  const isShort = useMediaQuery('(max-height: 1149px)', false, { getInitialValueInEffect: false });
  const compact = !!isDesktop && !!isShort;
  if (state.status !== 'authed') return null;
  const u = state.user;

  const slides: HeroSlide[] = [
    {
      image: heroBg, // заглушка до фото от Artem; портретный фон auth, кроп по центру
      imagePosition: 'center 30%',
      title: 'Добро пожаловать,',
      titleAccent: `${u.fullName}!`,
      subtitle: 'Ваш персональный доступ к инструментам, новостям и аналитике компании.',
      cta: <HeroPushButton />,
    },
  ];

  const menu = MENU.filter((m) => !m.needsSales || hasSales);

  return (
    <Container fluid px={isDesktop ? 28 : 16} py={compact ? 16 : (isDesktop ? 24 : 16)}>
      <Stack gap={compact ? 16 : (isDesktop ? 24 : 16)}>
        <HeroCarousel slides={slides} isDesktop={isDesktop ?? false} />

        {/* Две колонки по мокапу: слева меню кабинета, справа лента новостей.
            Источник новостей временно — парсинг-лента табачного рынка; когда
            появятся «Новости компании», заменится только источник данных. */}
        <Flex direction={isDesktop ? 'row' : 'column'} align="stretch" gap={compact ? 16 : (isDesktop ? 24 : 16)} wrap="nowrap">
          <Paper
            radius={16}
            p={compact ? 'lg' : (isDesktop ? 'xl' : 'md')}
            style={{ background: '#fff', boxShadow: '0 1px 4px rgb(20 32 27 / 6%)', flex: isDesktop ? '0 0 440px' : undefined }}
          >
            <Text fw={700} fz={compact ? 20 : 22} c="#14201B" mb={compact ? 8 : 'sm'}>Кабинет клиента</Text>
            <Stack gap={2}>
              {menu.map((m) => (
                <MenuItem key={m.to} to={m.to} label={m.label} sub={m.sub} icon={m.icon} compact={compact} />
              ))}
            </Stack>
          </Paper>

          <LatestNews isDesktop={isDesktop ?? false} compact={compact} />
        </Flex>

        <StatCards compact={compact} />

        <Group justify="space-between" wrap="wrap" gap="xs" px={4} pb={compact ? 0 : 8}>
          <Text fz={13} c="#6B7570">© ООО ТК Евразия, {new Date().getFullYear()}</Text>
          <Text fz={13} c="#6B7570">Есть вопросы? Свяжитесь с вашим менеджером</Text>
        </Group>
      </Stack>
    </Container>
  );
}
